import React, { useState } from 'react';
import { NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import {
  LayoutDashboard, Target, FileText, BarChart3, FolderKanban,
  ShieldCheck, PieChart, Settings, Landmark, Users, Bell,
  LogOut, Search, ChevronLeft, ChevronRight, Menu, X, Award
} from 'lucide-react';

const navItems = [
  { path: '/', icon: LayoutDashboard, label: 'Dashboard' },
  { path: '/strategy', icon: Target, label: 'Strategy & Planning' },
  { path: '/business-plans', icon: FileText, label: 'Business Plans' },
  { path: '/performance', icon: BarChart3, label: 'Performance & KPIs' },
  { path: '/projects', icon: FolderKanban, label: 'Projects Management' },
  { path: '/grants', icon: Award, label: 'Grant Management' },
  { path: '/risks', icon: ShieldCheck, label: 'Risk Management' },
  { path: '/reporting', icon: PieChart, label: 'Reporting & Analytics' },
  { divider: true },
  { path: '/admin', icon: Settings, label: 'Admin Setup', adminOnly: true },
  { path: '/governance', icon: Landmark, label: 'Governance & Admin', adminOnly: true },
  { path: '/users', icon: Users, label: 'User Management', adminOnly: true },
];

export default function Layout() {
  const { user, logout, isAdmin } = useAuth();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const navigate = useNavigate();
  const handleLogout = () => { logout(); navigate('/login'); };

  const SidebarContent = () => (
    <>
      <div className={`flex items-center gap-3 px-4 py-5 border-b border-white/10 ${collapsed ? 'justify-center' : ''}`}>
        <div className="w-10 h-10 rounded-xl bg-[#c8102e] flex items-center justify-center flex-shrink-0" style={{ boxShadow: '0 4px 15px rgba(200,16,46,0.4)' }}>
          <span className="text-white font-bold text-lg">S</span>
        </div>
        {!collapsed && <div><h1 className="text-white font-bold text-lg leading-tight">SiloGate</h1><p className="text-slate-400 text-[10px] uppercase tracking-widest">Mintek Platform</p></div>}
      </div>
      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {navItems.map((item, i) => {
          if (item.divider) return <div key={i} className="my-3 border-t border-white/10" />;
          if (item.adminOnly && !isAdmin()) return null;
          const Icon = item.icon;
          return (
            <NavLink key={item.path} to={item.path} end={item.path === '/'}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm transition-all duration-200 ${isActive ? 'bg-[#c8102e] text-white shadow-lg' : 'text-slate-300 hover:bg-white/10 hover:text-white'} ${collapsed ? 'justify-center px-3' : ''}`}
              style={({ isActive }) => isActive ? { boxShadow: '0 4px 15px rgba(200,16,46,0.4)' } : {}}
              onClick={() => setMobileOpen(false)}>
              <Icon size={18} strokeWidth={2} />
              {!collapsed && <span>{item.label}</span>}
            </NavLink>
          );
        })}
      </nav>
      <div className="p-3 border-t border-white/10">
        {!collapsed && <div className="flex items-center gap-3 px-3 py-2 rounded-xl bg-white/5 mb-2">
          <div className="w-8 h-8 rounded-full bg-[#c8102e]/30 flex items-center justify-center text-white text-xs font-bold">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
          <div className="flex-1 min-w-0"><p className="text-white text-sm font-medium truncate">{user?.fullName || 'User'}</p><p className="text-slate-400 text-[11px] truncate">{user?.roles?.[0]}</p></div>
        </div>}
        <button onClick={handleLogout} className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-red-400 hover:text-red-300 hover:bg-red-500/10 w-full transition-colors ${collapsed ? 'justify-center px-3' : ''}`}>
          <LogOut size={18} strokeWidth={2} />
          {!collapsed && <span>Sign Out</span>}
        </button>
      </div>
    </>
  );

  return (
    <div className="flex h-screen bg-slate-50">
      <aside className={`hidden lg:flex flex-col transition-all duration-300 ${collapsed ? 'w-[72px]' : 'w-64'} relative`}
        style={{ background: 'linear-gradient(180deg, #0c1b2e 0%, #0a1420 100%)' }}>
        <SidebarContent />
        <button onClick={() => setCollapsed(!collapsed)} className="absolute -right-3 top-20 w-6 h-6 rounded-full bg-white shadow-md border border-slate-200 flex items-center justify-center text-slate-400 hover:text-[#c8102e] z-10 text-xs transition-colors">
          {collapsed ? <ChevronRight size={12} /> : <ChevronLeft size={12} />}
        </button>
      </aside>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <aside className="relative w-64 flex flex-col z-10" style={{ background: 'linear-gradient(180deg, #0c1b2e, #0a1420)' }}>
            <SidebarContent />
          </aside>
        </div>
      )}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 bg-white border-b border-slate-200 flex items-center justify-between px-4 lg:px-6 flex-shrink-0">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileOpen(true)} className="lg:hidden p-2 rounded-lg hover:bg-slate-100 text-slate-600">
              <Menu size={20} />
            </button>
            <div className="hidden sm:flex items-center gap-2 pl-3 pr-4 py-2 bg-slate-50 border border-slate-200 rounded-xl w-72">
              <Search size={14} className="text-slate-400" />
              <input type="text" placeholder="Search objectives, KPIs, projects..." className="bg-transparent text-sm w-full focus:outline-none" />
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className="hidden md:inline text-xs text-slate-400 font-mono bg-slate-50 px-2 py-1 rounded">FY 2025/26</span>
            <button className="relative p-2 rounded-xl hover:bg-slate-100 transition-colors">
              <Bell size={18} className="text-slate-600" />
              <span className="absolute top-1.5 right-1.5 w-2 h-2 bg-[#c8102e] rounded-full" />
            </button>
            <div className="w-8 h-8 rounded-full bg-[#c8102e] flex items-center justify-center text-white text-xs font-bold">{user?.firstName?.[0]}{user?.lastName?.[0]}</div>
          </div>
        </header>
        <main className="flex-1 overflow-y-auto p-4 lg:p-6"><Outlet /></main>
      </div>
    </div>
  );
}
